<template>
    <div class="layout__header" v-bind="$attrs" v-if="!isHidden">
        <header class="header" :class="headerClasses" id="header" ref="header">
            <div
                class="header-banner-missing-userdata"
                v-if="isJWTUserDtoPresent && !JWTUserDto?.id_customer"
            >
                <i18n-t keypath="header.missingData" tag="span">
                    <template #field>
                        <router-link to="/auth/my-carconnex/my-company/company-info">
                            {{ i18n.t("header.myCompanyInfo") }}
                        </router-link>
                    </template>
                </i18n-t>
            </div>
            <div class="header__container" @transitionend="onHeaderHeightChange">
                <template v-if="headerMode === 'default'">
                    <router-link class="header__logo-link" to="/">
                        <img
                            class="logo"
                            :src="`/img/${brandLabel}/logos/logo.svg`"
                            :alt="`${brandLabel} logo`"
                        />
                        <img
                            class="logo-color"
                            :src="`/img/${brandLabel}/logos/logo-color.svg`"
                            :alt="`${brandLabel} logo`"
                        />
                    </router-link>
                    <div class="header__btns" v-if="!shouldHideNavigationAndButtons">
                        <router-link
                            :to="{
                                path: '/auth/vehicles-grouped',
                                query: fetchVehiclesQueryForLink,
                            }"
                            v-slot="{ navigate, href }"
                            custom
                            v-if="
                                routerModel.regionsByTemplate['button-page'].data[
                                    appLanguageSwitcherModel.lang
                                ]
                            "
                        >
                            <a
                                class="button button--md button--hidden-prefix"
                                v-app-auth-required="{ role: 'supplier' }"
                                v-app-header-btn
                                :href="href"
                                @click.prevent="showCompanyStatusDialog() || navigate()"
                            >
                                <span>
                                    {{
                                        routerModel.regionsByTemplate["button-page"].data[
                                            appLanguageSwitcherModel.lang
                                        ].buy
                                    }}
                                </span>
                                <span class="visible-on-hover">
                                    <span class="letter" :style="{ '--i': 0 }">
                                        &nbsp;
                                    </span>
                                    <span
                                        class="letter"
                                        :style="{ '--i': index + 1 }"
                                        v-bind:key="'buy' + index"
                                        v-for="(
                                            char, index
                                        ) in routerModel.regionsByTemplate[
                                            'button-page'
                                        ].data[appLanguageSwitcherModel.lang].cars?.split(
                                            '',
                                        )"
                                    >
                                        {{ char }}
                                    </span>
                                </span>
                            </a>
                        </router-link>

                        <router-link
                            :to="`/${lang}/work-in-progress`"
                            v-slot="{ navigate, href }"
                            custom
                            v-if="
                                routerModel.regionsByTemplate['button-page'].data[
                                    appLanguageSwitcherModel.lang
                                ]
                            "
                        >
                            <a
                                class=" button button--white button--md button--hidden-prefix"
                                v-app-header-btn
                                v-app-auth-required="{ role: 'supplier' }"
                                :href="href"
                                @click.prevent="showCompanyStatusDialog() || navigate()"
                            >
                                <span>
                                    {{
                                        routerModel.regionsByTemplate["button-page"].data[
                                            appLanguageSwitcherModel.lang
                                        ].sell
                                    }}
                                </span>
                                <span class="visible-on-hover">
                                    <span class="letter" :style="{ '--i': 0 }">
                                        &nbsp;
                                    </span>
                                    <span
                                        class="letter"
                                        v-bind:key="'sell' + index"
                                        :style="{ '--i': index + 1 }"
                                        v-for="(
                                            char, index
                                        ) in routerModel.regionsByTemplate[
                                            'button-page'
                                        ].data[appLanguageSwitcherModel.lang].cars?.split(
                                            '',
                                        )"
                                    >
                                        {{ char }}
                                    </span>
                                </span>
                            </a>
                        </router-link>
                    </div>

                    <navigation-buttons
                        v-if="!isMobile"
                        :isVehicleDetails="isVehicleDetails"
                        :navigateToVehicle="navigateToVehicle"
                        :isFirstVehicleInList="isFirstVehicleInList"
                        :isLastVehicleInList="isLastVehicleInList"
                    />
                </template>

                <div
                    class="main-menu__overlay"
                    @click="() => headerModel.toggleSidebar(false)"
                />
                <div
                    class="main-menu__wrapper"
                    :class="{
                        header__nav: !isVehicleDetails,
                        'header__nav-vehicle-details': isVehicleDetails,
                    }"
                >
                    <div class="main-menu__header" v-if="isJWTUserDtoPresent">
                        <a class="settings-link" @click="routeToDashboard()">
                            <span v-if="userAvatarPresent" class="settings-link__circle">
                                <img
                                    :src="sessionModel.userAvatar.value"
                                    style="object-fit: cover; width: 100%; height: 100%"
                                />
                            </span>
                            <span v-else class="settings-link__circle">
                                <svg class="sprite-icon profile-user">
                                    <use xlink:href="/img/sprite.svg#profile-user"></use>
                                </svg>
                            </span>
                            <span class="word-8-truncate">
                                {{ JWTUserDto?.family_name }}
                            </span>
                        </a>
                    </div>
                    <div class="main-menu__header" v-else>
                        <router-link class="settings-link" to="/login">
                            <span class="settings-link__circle">
                                <svg class="sprite-icon">
                                    <use xlink:href="/img/sprite.svg#profile-user"></use>
                                </svg>
                            </span>
                            <span>
                                <i18n-t keypath="header.brandName">
                                    <template v-slot:brandName>
                                        <span>{{ brandName }}</span>
                                    </template>
                                </i18n-t>
                            </span>
                        </router-link>
                    </div>
                    <nav v-if="headerMode !== 'vehicle-details'" class="main-menu__body">
                        <ul
                            class="main-menu"
                            :style="{ marginBottom: isMobile && isJWTUserDtoPresent ? '100px' : '0' }"
                            :class="{
                                active: $route.path.startsWith('/auth/vehicle-details'),
                            }"
                        >
                            <template
                                v-for="item in routerModel.regionsByTemplate['header']
                                    .data[appLanguageSwitcherModel.lang]?.links"
                                v-bind:key="item.id"
                            >
                                <router-link
                                    v-if="
                                        routerModel.routes[appLanguageSwitcherModel.lang][
                                            item.page
                                        ]
                                    "
                                    v-slot="{ isExactActive, navigate }"
                                    :to="
                                        routerModel.routes[appLanguageSwitcherModel.lang][
                                            item.page
                                        ].path
                                    "
                                    custom
                                >
                                    <li class="main-menu__item">
                                        <router-link
                                            :to="
                                                routerModel.routes[
                                                    appLanguageSwitcherModel.lang
                                                ][item.page].path
                                            "
                                            :class="{ 'is-current': isExactActive }"
                                            @click.prevent="
                                                () => navigateAndCloseSidebar(navigate)
                                            "
                                        >
                                            <svg class="sprite-icon main-menu__icon">
                                                <use
                                                    xlink:href="/img/sprite.svg#carconnex"
                                                ></use>
                                            </svg>
                                            <span>
                                                {{ item.name }}
                                            </span>
                                        </router-link>
                                    </li>
                                </router-link>
                            </template>
                        </ul>
                        <ul
                            v-if="isMobile && isJWTUserDtoPresent"
                            class="main-menu"
                            :class="{
                                active: $route.path.startsWith('/auth/vehicle-details'),
                            }"
                        >
                            <template v-for="(item, key) in ctx.mobileMenu" :key="key">
                                <router-link
                                    v-slot="{ isExactActive, navigate }"
                                    :to="item.href"
                                    custom
                                >
                                    <li class="main-menu__item">
                                        <a
                                            :href="item.path"
                                            @click.prevent="
                                                navigateAndCloseSidebar(navigate)
                                            "
                                        >
                                            <svg
                                                class="sprite-icon main-menu__icon"
                                                :class="[
                                                    item.class,
                                                    isExactActive ? 'is-current' : '',
                                                ]"
                                                width="20"
                                                height="20"
                                            >
                                                <use
                                                    :xlink:href="`/img/sprite.svg#${item.sprite}`"
                                                ></use>
                                            </svg>
                                            <span><i18n-t :keypath="item.i18n" /></span>
                                        </a>
                                    </li>
                                </router-link>
                            </template>
                            <li
                                class="main-menu__item --log-out"
                                @click="ctx.logout()"
                            >
                                        <a href="#">
                                            <svg
                                                class="sprite-icon main-menu__icon"
                                                width="20"
                                                height="20"
                                            >
                                                <use xlink:href="/img/sprite.svg#logout"></use>
                                            </svg>
                                            <span>
                                                <i18n-t keypath="mobileMenu.logOut" />
                                            </span>
                                    </a>
                            </li>
                        </ul>
                    </nav>
                    <div class="main-menu__footer" v-if="!shouldHideNavigationAndButtons">
                        <router-link
                            to="/auth/vehicles-grouped"
                            v-slot="{ href, navigate }"
                            custom
                            v-if="
                                routerModel.regionsByTemplate['button-page'].data[
                                    appLanguageSwitcherModel.lang
                                ]
                            "
                        >
                            <a
                                v-app-auth-required="{ role: 'supplier' }"
                                class="button button--md"
                                :href="href"
                                @click.prevent="
                                    showCompanyStatusDialog() ||
                                        navigateAndCloseSidebar(navigate)
                                "
                            >
                                <span><i18n-t keypath="header.buyCar" /></span>
                            </a>
                        </router-link>
                        <router-link
                            to="/work-in-progress"
                            v-slot="{ href, navigate }"
                            custom
                        >
                            <a
                                v-app-auth-required="{ role: 'supplier' }"
                                class="button button--white button--md"
                                :href="href"
                                @click.prevent="
                                    showCompanyStatusDialog(true) ||
                                        navigateAndCloseSidebar(navigate)
                                "
                            >
                                <span><i18n-t keypath="header.sellCar" /></span>
                            </a>
                        </router-link>
                    </div>
                </div>

                <app-language-switcher></app-language-switcher>
                <a
                    v-if="isManyNews"
                    class="header__notification-link"
                    @click.prevent="() => toggleNotifications()"
                >
                    <div class="js-animated-notification">
                        <svg class="sprite-icon bell">
                            <use xlink:href="/img/sprite.svg#bell"></use>
                        </svg>
                    </div>
                </a>
                <template v-if="isJWTUserDtoPresent">
                    <div class="header__divider"></div>
                    <router-link
                        v-if="!isMobile"
                        class="header__favorites-link"
                        to="/auth/my-carconnex/my-favorites"
                    >
                        <svg class="sprite-icon">
                            <use xlink:href="/img/sprite.svg#heart-fill"></use>
                        </svg>
                    </router-link>
                    <a
                        class="header__cart-link cart-link"
                        style="margin-top: 2px"
                        :class="{ 'is-open': showCart }"
                        v-if="isApprovedCompany && canUseCart"
                    >
                        <router-link to="/shopping-cart">
                            <div class="cart-link__icon">
                                <svg class="sprite-icon">
                                    <use xlink:href="/img/sprite.svg#cart"></use>
                                </svg>
                            </div>
                            <div
                                v-if="cartModel.items.length > 0"
                                class="cart-link__baloon"
                            >
                                {{ cartModel.items.length }}
                            </div>
                        </router-link>
                    </a>
                    <div class="header__dropdown">
                        <router-link
                            class="header__btn header__btn_user"
                            to="/auth/my-carconnex"
                        >
                            <span>
                                <span>
                                    <i18n-t keypath="common.hello" />
                                    ,&nbsp;
                                </span>
                                <span class="word-8-truncate" v-if="isJWTUserDtoPresent">
                                    {{ JWTUserDto?.given_name }}
                                </span>
                            </span>
                            <i class="avatar" v-if="sessionModel.userHasAvatar">
                                <img :src="sessionModel.userAvatar.value" />
                            </i>
                        </router-link>
                        <div
                            v-if="isJWTUserDtoPresent"
                            class="header__dropdown_list"
                            :style="{ 'min-width': 'max-content' }"
                        >
                            <template
                                v-for="(item, key) in ctx.headerDropdownMenu"
                                :key="key"
                            >
                                <router-link
                                    v-slot="{ isExactActive, navigate }"
                                    v-if="
                                        !(item.onlyApproveCompany && !isApprovedCompany)
                                    "
                                    :to="item.href"
                                    custom
                                >
                                    <a :href="item.path" @click.prevent="navigate()">
                                        <li
                                            class="header__dropdown_block"
                                            :class="isExactActive ? 'active' : ''"
                                        >
                                            <svg
                                                class="header__dropdown_icon"
                                                width="20"
                                                height="20"
                                            >
                                                <use
                                                    :xlink:href="`/img/sprite.svg#${item.sprite}`"
                                                ></use>
                                            </svg>
                                            <span class="header__dropdown_text">
                                                <i18n-t :keypath="item.i18n" />
                                            </span>
                                        </li>
                                    </a>
                                </router-link>
                            </template>
                            <div class="header__dropdown_separator" />
                            <div class="header__dropdown_block" @click="ctx.logout()">
                                <svg class="header__dropdown_icon" width="20" height="20">
                                    <use xlink:href="/img/sprite.svg#log-out"></use>
                                </svg>
                                <span class="header__dropdown_text">
                                    <i18n-t keypath="dashboardSidebar.logOut" />
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
                <router-link v-else class="header__btn header__btn_user" to="/login">
                    <span>
                        <i18n-t keypath="header.brandName">
                            <template v-slot:brandName>
                                <span>{{ brandName }}</span>
                            </template>
                        </i18n-t>
                    </span>
                    <svg class="sprite-icon profile-user">
                        <use xlink:href="/img/sprite.svg#profile-user"></use>
                    </svg>
                </router-link>
                <div
                    class="c-pointer menu-trigger js-menu-trigger"
                    @click.stop="headerModel.toggleSidebar()"
                >
                    <span></span>
                </div>
            </div>
            <navigation-buttons
                :class="{ 'header-buttons-row': isVehicleDetails }"
                v-if="isMobile"
                :isVehicleDetails="isVehicleDetails"
                :navigateToVehicle="navigateToVehicle"
                :isFirstVehicleInList="isFirstVehicleInList"
                :isLastVehicleInList="isLastVehicleInList"
            />
        </header>
        <!--END header-->
    </div>
    <notification-component v-model:active="isNotificationsOpen" />
</template>

<script lang="ts">
import { HeaderComponent } from "./base-header";

export default HeaderComponent;

</script>

<style scoped lang="scss">
@import "../../../shared/styles/vars";
.header-buttons-row {
    height: 25px;
    background-color: $color-white;
    justify-content: space-around;
}
.header-banner-missing-userdata {
    width: 100%;
    text-align: center;
    background-color: var(--color-primary);
}
.header-carDetail__btn.header-carDetail__btn_prev {
    display: flex;
    align-items: center;
    padding: 5px;
    height: 25px;
    margin-top: -2px;
    font-size: 10px;
    width: 77px;
    border: 1px rgba(98, 118, 144, 0.3);
}

.header-carDetail__btn.header-carDetail__btn_next {
    display: flex;
    align-items: center;
    padding: 5px;
    height: 25px;
    margin-top: -2px;
    font-size: 10px;
    width: 77px;
    border: 1px rgba(98, 118, 144, 0.3);
}

.sprite-icon.fc-icon-chevron-right {
    fill: #627690;
    stroke: #627690;
    height: 16px;
}
.sprite-icon.fc-icon-chevron-left {
    fill: #627690;
    stroke: #627690;
    height: 16px;
}

.button--hidden-prefix {
    .letter {
        opacity: 0;
        transform: scale(0);
        max-width: 0;
        display: inline-block;
    }

    &:hover {
        .letter {
            animation: hoverButton 0.5s ease-out calc(0.02s * var(--i));
            animation-fill-mode: forwards;
        }
    }
    &:not(:hover) {
        .letter {
            animation: notHoverButton calc(0.5s / var(--i)) ease-out calc(0.2s / var(--i));
            animation-fill-mode: backwards;
        }
    }
}

@keyframes hoverButton {
    0% {
        opacity: 0;
        transform: scale(0);
        max-width: 0;
    }
    100% {
        opacity: 1;
        transform: scale(1);
        max-width: 20px;
    }
}
@keyframes notHoverButton {
    0% {
        opacity: 1;
        transform: scale(1);
        max-width: 20px;
    }
    100% {
        opacity: 0;
        transform: scale(0);
        max-width: 0;
    }
}
</style>